import { isEmpty, pick } from 'es-toolkit/compat';
import { createContext, ReactNode, useEffect, useMemo } from 'react';
import { isEmptyRecord } from 'shared/helpers/CommonHelpers';
import { useNullableContext } from 'shared/hooks/useNullableContext';
import { UpdateParams, useLocationParams } from 'shared/routing/LocationParams';
import { useFlag } from 'shared/settings/FeatureToggles';
import { trackLoadsEvent } from '../../data/LoadsAnalytics';
import {
  LoadsPageParams,
  loadsPageParamsSchema,
  LOAD_FILTER_FIELDS,
  LOAD_SEARCH_FIELDS,
} from '../../data/LoadsPageParams';

interface LoadsPageParamsContext {
  isEmptyFilterParams: boolean;
  isEmptySearchParams: boolean;

  params: LoadsPageParams;
  updateParams: UpdateParams<LoadsPageParams>;
}

const Context = createContext<null | LoadsPageParamsContext>(null);

export function useLoadsPageContext() {
  return useNullableContext('LoadsPageContext', Context);
}

interface LoadsPageContextProviderProps {
  children?: ReactNode;
  shouldPersistParams?: boolean;
}

export function LoadsPageContextProvider({
  children,
  shouldPersistParams,
}: LoadsPageContextProviderProps) {
  const isPaginationEnabled = useFlag('carrier_loads_pagination');
  const [locationParams, updateParams] = useLocationParams({
    zodSchema: loadsPageParamsSchema,
    persistName: shouldPersistParams ? 'LoadsPage' : undefined,
    onParseSessionParams: (prevParams: LoadsPageParams) => {
      if (prevParams.stage === 'active' || prevParams.stage === 'payable') {
        return {} as LoadsPageParams;
      }

      return prevParams;
    },
  });

  // Without the flag the page size stays at its default, whatever the URL holds
  const params = useMemo<LoadsPageParams>(
    () =>
      isPaginationEnabled
        ? locationParams
        : { ...locationParams, page_size: null },
    [isPaginationEnabled, locationParams],
  );

  const filterParams = useMemo(
    () => pick(params, LOAD_FILTER_FIELDS),
    [params],
  );

  useEffect(() => {
    if (!isEmptyRecord(filterParams)) {
      trackLoadsEvent({ name: 'CTMS: Filtered Loads', params: filterParams });
    }
  }, [filterParams]);

  const ctx = useMemo<LoadsPageParamsContext>(() => {
    const isEmptyFilterParams = !LOAD_FILTER_FIELDS.find(
      (key) => !isEmpty(params[key]),
    );
    const isEmptySearchParams = !LOAD_SEARCH_FIELDS.find(
      (key) => !isEmpty(params[key as keyof typeof params]),
    );

    return { params, updateParams, isEmptyFilterParams, isEmptySearchParams };
  }, [params, updateParams]);

  return <Context.Provider value={ctx}>{children}</Context.Provider>;
}
